import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import StepIndicator from '@/components/booking/StepIndicator';
import ServiceSelection from '@/components/booking/ServiceSelection';
import PropertyDetails from '@/components/booking/PropertyDetails';
import PriceSummary from '@/components/booking/PriceSummary';
import AppointmentSelection from '@/components/booking/AppointmentSelection';
import CustomerInfo from '@/components/booking/CustomerInfo';
import PaymentSelection from '@/components/booking/PaymentSelection';
import { API } from '@/utils/api';

const Booking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const cardTitleRef = useRef(null);

  const [bookingData, setBookingData] = useState({
    serviceId: searchParams.get('service') || '',
    serviceName: '',
    propertyType: '',
    rooms: 5,
    sqm: 150,
    postcode: '',
    extras: {},
    uValueOptions: {},
    preferredDate: '',
    preferredTime: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    accessNotes: '',
    paymentMethod: '',
    pricing: null,
    // Add-on services
    selectedAddons: [],
    sapTotalUValues: 3, // 3 included in base SAP price
    pricingBreakdown: null
  });

  useEffect(() => {
    fetchServices();
    // fetchServices reads bookingData.serviceId from initial mount only;
    // running once on mount is intentional.
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);

      // If service pre-selected, populate name and skip to step 2
      if (bookingData.serviceId) {
        const service = response.data.find(s => s.id === bookingData.serviceId);
        if (service) {
          setBookingData(prev => ({ ...prev, serviceName: service.name }));
          // Auto-skip to step 2 (property details) when service is pre-selected
          setCurrentStep(2);
          // Scroll to top
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  }, [bookingData.serviceId]);

  const updateBookingData = (updates) => {
    setBookingData(prev => ({ ...prev, ...updates }));
  };

  const calculatePrice = async (addonsUpdate = null) => {
    try {
      setLoading(true);
      
      // Use passed addons or current state
      const selectedAddons = addonsUpdate?.selectedAddons ?? bookingData.selectedAddons;
      const sapTotalUValues = addonsUpdate?.sapTotalUValues ?? bookingData.sapTotalUValues;
      
      const response = await axios.post(`${API}/calculate-price`, {
        service_id: bookingData.serviceId,
        rooms: bookingData.rooms,
        sqm: bookingData.sqm,
        postcode: bookingData.postcode,
        extras: bookingData.extras,
        u_value_options: bookingData.uValueOptions,
        selected_addons: selectedAddons,
        sap_total_u_values: sapTotalUValues
      });
      
      updateBookingData({ 
        pricing: response.data,
        pricingBreakdown: response.data.breakdown,
        ...(addonsUpdate || {})
      });
      return true;
    } catch (error) {
      console.error('Error calculating price:', error);
      toast.error('Failed to calculate price');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handler for add-on changes from PriceSummary
  const handleAddonsChange = async (addonsUpdate) => {
    await calculatePrice(addonsUpdate);
  };

  const submitBooking = async (paypalData = null) => {
    try {
      setLoading(true);
      
      const selectedService = services.find(s => s.id === bookingData.serviceId);
      const isDigitalService = selectedService && !selectedService.requires_visit;
      
      const bookingPayload = {
        service_id: bookingData.serviceId,
        service_name: bookingData.serviceName,
        customer_name: bookingData.customerName,
        customer_email: bookingData.customerEmail,
        customer_phone: bookingData.customerPhone,
        address_line1: bookingData.addressLine1,
        address_line2: bookingData.addressLine2 || null,
        city: bookingData.city,
        postcode: bookingData.postcode,
        property_type: bookingData.propertyType,
        rooms: bookingData.rooms || null,
        sqm: bookingData.sqm || null,
        extras: bookingData.extras,
        // For digital services, use placeholder date/time
        preferred_date: isDigitalService ? new Date().toISOString().split('T')[0] : bookingData.preferredDate,
        preferred_time: isDigitalService ? '09:00' : bookingData.preferredTime,
        access_notes: bookingData.accessNotes || null,
        payment_method: bookingData.paymentMethod,
        total_price: bookingData.pricing.total_price,
        travel_surcharge: bookingData.pricing.travel_surcharge,
        // Add-on services
        selected_addons: bookingData.selectedAddons.length > 0 ? bookingData.selectedAddons : null,
        sap_total_u_values: bookingData.serviceId === 'sap_calculation' ? bookingData.sapTotalUValues : null,
        pricing_breakdown: bookingData.pricing.breakdown || null,
        // PayPal data (if paid via PayPal)
        ...(paypalData ? {
          paypal_order_id: paypalData.orderId,
          paypal_capture_id: paypalData.captureId,
          paypal_payment_status: paypalData.status
        } : {})
      };

      const response = await axios.post(`${API}/bookings`, bookingPayload);
      
      setBookingReference(response.data.booking_reference);
      setBookingComplete(true);
      setCurrentStep(7);
      toast.success(paypalData ? 'Payment received — booking confirmed!' : 'Booking confirmed!');
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalSuccess = (paypalData) => {
    submitBooking(paypalData);
  };

  const handleNext = async () => {
    const selectedService = services.find(s => s.id === bookingData.serviceId);
    const isDigitalService = selectedService && !selectedService.requires_visit;
    
    // Validation and logic for each step
    if (currentStep === 1) {
      if (!bookingData.serviceId) {
        toast.error('Please select a service');
        return;
      }
    }
    
    if (currentStep === 2) {
      if (!bookingData.addressLine1 || !bookingData.city || !bookingData.postcode || !bookingData.propertyType) {
        toast.error('Please fill in all required fields (Address, City, Postcode, Property Type)');
        return;
      }
      // Check for large property
      if (bookingData.rooms && bookingData.rooms > 25) {
        toast.error('For properties with more than 25 rooms, please contact us for a custom quote');
        return;
      }
      // Calculate price before moving to summary
      const success = await calculatePrice();
      if (!success) return;
    }
    
    if (currentStep === 3 && isDigitalService) {
      // Skip appointment selection for digital services (step 4)
      // Go directly to step 5 (customer info)
      setCurrentStep(5);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (cardTitleRef.current) {
          cardTitleRef.current.focus();
        }
      }, 50);
      return;
    }
    
    if (currentStep === 4) {
      if (!bookingData.preferredDate || !bookingData.preferredTime) {
        toast.error('Please select a date and time');
        return;
      }
    }
    
    if (currentStep === 5) {
      if (!bookingData.customerName || !bookingData.customerEmail || !bookingData.customerPhone) {
        toast.error('Please fill in all customer details');
        return;
      }
    }
    
    if (currentStep === 6) {
      if (!bookingData.paymentMethod) {
        toast.error('Please select a payment method');
        return;
      }
      // Submit booking
      await submitBooking();
      return;
    }
    
    setCurrentStep(prev => prev + 1);
    // Scroll to top on step change and focus on card title
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (cardTitleRef.current) {
        cardTitleRef.current.focus();
      }
    }, 50);
  };

  const handleBack = () => {
    const selectedService = services.find(s => s.id === bookingData.serviceId);
    const isDigitalService = selectedService && !selectedService.requires_visit;
    
    // If on step 5 (customer info) and it's a digital service, skip back to step 3 (price summary)
    if (currentStep === 5 && isDigitalService) {
      setCurrentStep(3);
    } else {
      setCurrentStep(prev => prev - 1);
    }
    
    // Scroll to top on step change and focus on card title
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (cardTitleRef.current) {
        cardTitleRef.current.focus();
      }
    }, 50);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ServiceSelection 
            services={services}
            selectedService={bookingData.serviceId}
            onSelect={(serviceId, serviceName) => updateBookingData({ serviceId, serviceName })}
          />
        );
      case 2:
        return (
          <PropertyDetails 
            bookingData={bookingData}
            services={services}
            onUpdate={updateBookingData}
          />
        );
      case 3:
        return (
          <PriceSummary 
            bookingData={bookingData}
            priceData={bookingData.pricing}
            onAddonsChange={handleAddonsChange}
          />
        );
      case 4:
        return (
          <AppointmentSelection 
            bookingData={bookingData}
            onUpdate={updateBookingData}
          />
        );
      case 5:
        return (
          <CustomerInfo 
            bookingData={bookingData}
            onUpdate={updateBookingData}
          />
        );
      case 6:
        return (
          <PaymentSelection 
            bookingData={bookingData}
            onUpdate={updateBookingData}
            totalPrice={bookingData.pricing?.total_price}
            onPayPalSuccess={handlePayPalSuccess}
          />
        );
      case 7:
        return (
          <div className="text-center py-12" data-testid="booking-confirmation">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed!</h2>
            <p className="text-lg text-gray-600 mb-2">Your booking reference:</p>
            <p className="text-2xl font-bold text-green-600 mb-6" data-testid="booking-reference">{bookingReference}</p>
            <div className="max-w-2xl mx-auto bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-gray-700 text-left space-y-2">
                <li>• You will receive a confirmation email at {bookingData.customerEmail}</li>
                <li>• We will contact you to confirm your appointment</li>
                {bookingData.paymentMethod === 'bacs' && (
                  <li>• BACS payment details will be provided on your invoice</li>
                )}
                <li>• Your assessment will be emailed once completed</li>
              </ul>
            </div>
            
            {/* Review CTA */}
            <div className="max-w-2xl mx-auto bg-gradient-to-br from-green-50 to-cyan-50 border border-green-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-2">Enjoyed Our Service?</h3>
              <p className="text-sm text-gray-700 mb-4">
                After your assessment, we'd love to hear your feedback! Your review helps others choose the right energy assessor.
              </p>
              <Button 
                onClick={() => navigate('/reviews')}
                className="text-white"
                style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                data-testid="booking-leave-review-btn"
              >
                Leave Us a Review
              </Button>
            </div>
            
            <div className="space-x-4">
              <Button 
                data-testid="booking-home-btn"
                onClick={() => navigate('/')} 
                variant="outline"
              >
                Return Home
              </Button>
              <Button 
                data-testid="booking-new-btn"
                onClick={() => window.location.reload()} 
                className="text-white"
                style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
              >
                Make Another Booking
              </Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO
        title="Book Your Energy Assessment | EPC & SAP Services"
        description="Book your EPC, SAP calculation, or energy assessment online. Choose your service, pick a date, and receive instant confirmation. Serving Exeter and the South West."
        canonical="/booking"
      />
      <Navbar />

      <section className="py-8" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center text-white" data-testid="booking-heading">Book Your Service</h1>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          {!bookingComplete && (
            <StepIndicator currentStep={currentStep} />
          )}

          <Card className="mt-8 shadow-lg border-none">
            <CardContent className="p-6 md:p-8">
              {/* Top Navigation Buttons */}
              {!bookingComplete && (
                <div className="flex justify-between mb-6 pb-6 border-b">
                  <Button
                    data-testid="booking-back-btn-top"
                    onClick={handleBack}
                    disabled={currentStep === 1 || loading}
                    variant="outline"
                    className="px-8"
                  >
                    Back
                  </Button>
                  <h2 
                    ref={cardTitleRef}
                    tabIndex={-1}
                    className="text-xl font-bold text-gray-900 outline-none"
                  >
                    {currentStep === 1 && 'Select Service'}
                    {currentStep === 2 && 'Property Details'}
                    {currentStep === 3 && 'Price Summary'}
                    {currentStep === 4 && 'Select Appointment'}
                    {currentStep === 5 && 'Your Information'}
                    {currentStep === 6 && 'Payment Method'}
                  </h2>
                  <Button
                    data-testid="booking-next-btn-top"
                    onClick={handleNext}
                    disabled={loading}
                    className="text-white px-8"
                    style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : currentStep === 6 ? 'Confirm Booking' : 'Next'}
                  </Button>
                </div>
              )}
              
              {renderStep()}
            </CardContent>
          </Card>

          {!bookingComplete && (
            <div className="flex justify-between mt-8">
              <Button
                data-testid="booking-back-btn"
                onClick={handleBack}
                disabled={currentStep === 1 || loading}
                variant="outline"
                className="px-8"
              >
                Back
              </Button>
              <Button
                data-testid="booking-next-btn"
                onClick={handleNext}
                disabled={loading || (currentStep === 6 && bookingData.paymentMethod === 'paypal')}
                className="text-white px-8"
                style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : currentStep === 6 ? (
                  bookingData.paymentMethod === 'paypal' ? 'Pay with PayPal above' : 'Confirm Booking'
                ) : (
                  'Next'
                )}
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Booking;